// Slice 2 Checkpoint 9 · Phase 2 — Admin render primitive (A2).
//
// Single source of truth for the admin SPA's loading / error / empty
// render shape. Every `*Page` in `apps/admin/src/pages/**` that renders
// a `FetchResult<T>` delegates the three pre-ok branches to this
// primitive; the caller retains full control of the ok branch via the
// `children(artifact)` render prop.
//
// Behavior preserved byte-identically from CP1–CP8 page code:
//   - Loading block classes:
//       'rounded-md border border-slate-800 bg-fishstank-surface p-6
//        text-sm text-slate-400'
//     Testid: `${testidPrefix}-loading`. Body: `labels.loading` verbatim.
//   - Error block classes:
//       'rounded-md border border-red-900 bg-red-950/40 p-4 text-sm
//        text-red-200'
//     Testid: `${testidPrefix}-error`.
//     Body structure (verbatim from Entities/Wiki/Events today):
//       <p className="font-semibold">{labels.errorTitle}</p>
//       <p className="mt-1 text-red-300">
//         {result.message}
//         {result.status && (
//           <span className="ml-2 font-mono">(HTTP {result.status})</span>
//         )}
//       </p>
//   - Empty block classes:
//       'rounded-md border border-slate-800 bg-fishstank-surface p-6
//        text-sm text-slate-400'
//     Testid: `${testidPrefix}-empty`. Body: `labels.empty` verbatim.
//
// The `labels.empty` field is intentionally optional. When omitted, the
// `kind:'empty'` case falls through to `children(result.artifact)` so
// the caller can render a CTA-bearing empty state of its own (see
// `pages/Events.tsx` → `AdminEventsTable`'s inline "No events yet. Use
// 'Create event' above to add the first one." — that CTA empty is NOT
// a plain empty and is kept at the table layer per the CP9 plan §
// "Phase 2 — A2 render primitive").
//
// Grounding: FISHSTANK_V2_SLICE2_CHECKPOINT_9_PLAN.md § "Phase 2 — A2
// render primitive"; FISHSTANK_V2_ADMIN_CONSOLIDATION_NOTES.md § A2.

import type { ReactNode } from 'react'
import type { FetchResult } from '../lib/adminFetch'

export interface FetchedArtifactLabels {
  /**
   * Copy shown inside the loading block. Example: `'Loading entities…'`.
   * Must be a non-empty string; rendered verbatim.
   */
  loading: string
  /**
   * Bold title shown inside the error block. Example:
   * `"Couldn't load entities."`. Rendered verbatim — callers keep their
   * existing punctuation + capitalization.
   */
  errorTitle: string
  /**
   * Optional. Copy shown inside the empty block. When set (Entities,
   * Wiki), the `kind:'empty'` case renders a dedicated empty block.
   * When unset (Events), the `kind:'empty'` case falls through to
   * `children(artifact)` so the caller can render a CTA-bearing empty
   * inside its own component tree.
   */
  empty?: string
}

export interface FetchedArtifactProps<T> {
  /**
   * The discriminated union returned by `doAdminReadFetch<T>`.
   * `undefined` during the initial query before react-query resolves.
   */
  result: FetchResult<T> | undefined
  /**
   * Passes through `useQuery().isLoading`. When true, the loading block
   * is rendered regardless of `result` state — matches the prior
   * `query.isLoading && (...)` guard in all three pages.
   */
  isLoading: boolean
  /**
   * Per-surface prefix for `data-testid` values. Example: `'entities'`
   * produces `entities-loading`, `entities-error`, `entities-empty`.
   * The inventory of prefixes is: entities, wiki, events (Events'
   * `events-empty` stays inside `AdminEventsTable` because its copy is
   * CTA-bearing — see module-level comment).
   */
  testidPrefix: string
  /**
   * Per-surface copy. `loading` and `errorTitle` required; `empty`
   * optional (see `FetchedArtifactLabels.empty`).
   */
  labels: FetchedArtifactLabels
  /**
   * Ok-branch render prop. Receives the `artifact` unchanged. Invoked
   * on `kind:'ok'` and — when `labels.empty` is undefined — also on
   * `kind:'empty'` (so callers that want CTA-bearing empties can opt
   * out of the built-in empty block).
   */
  children: (artifact: T) => ReactNode
}

/**
 * Admin render primitive for `FetchResult<T>`. Collapses the
 * loading / error / empty boilerplate that Entities.tsx, Wiki.tsx, and
 * Events.tsx duplicated across CP1–CP8. The `children(artifact)` render
 * prop owns the ok branch entirely — this primitive never touches
 * tables, tables' summaries, or per-row rendering.
 */
export function FetchedArtifact<T>({
  result,
  isLoading,
  testidPrefix,
  labels,
  children,
}: FetchedArtifactProps<T>): ReactNode {
  if (isLoading) {
    return (
      <div
        data-testid={`${testidPrefix}-loading`}
        className="rounded-md border border-slate-800 bg-fishstank-surface p-6 text-sm text-slate-400"
      >
        {labels.loading}
      </div>
    )
  }
  if (!result) {
    // Not loading, no data yet — matches the pre-CP9 shape where none of
    // the three pages rendered anything in this state. (react-query
    // guarantees `isLoading === true` until the first settle, so in
    // practice this branch is only reachable if a caller passes
    // `isLoading === false` before a `result` arrives.)
    return null
  }
  if (result.kind === 'error') {
    return (
      <div
        data-testid={`${testidPrefix}-error`}
        className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm text-red-200"
      >
        <p className="font-semibold">{labels.errorTitle}</p>
        <p className="mt-1 text-red-300">
          {result.message}
          {result.status && (
            <span className="ml-2 font-mono">(HTTP {result.status})</span>
          )}
        </p>
      </div>
    )
  }
  if (result.kind === 'empty' && labels.empty !== undefined) {
    return (
      <div
        data-testid={`${testidPrefix}-empty`}
        className="rounded-md border border-slate-800 bg-fishstank-surface p-6 text-sm text-slate-400"
      >
        {labels.empty}
      </div>
    )
  }
  // kind:'ok' — always passes artifact to children.
  // kind:'empty' with `labels.empty` undefined — also passes artifact
  //   to children (opt-out: caller renders its own CTA empty).
  return <>{children(result.artifact)}</>
}
